import { useState } from 'react';
import { Camera, Save } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getInitials } from '../lib/utils';

export function Profile() {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    bio: user?.bio || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditing(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 text-slate-900 font-body antialiased">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mb-2">Profile Settings</h1>
        <p className="text-slate-500 text-sm font-sans mb-8">Manage your account profile and learning preferences</p>

        {/* Profile Card */}
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-8 text-center sm:text-left">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-24 h-24 rounded-3xl border-2 border-slate-200 object-cover shadow-xs"
              />
            ) : (
              <div className="w-24 h-24 bg-blue-600 rounded-3xl flex items-center justify-center shadow-sm border border-blue-500">
                <span className="text-3xl font-heading font-extrabold text-white">
                  {getInitials(user?.name || '')}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900">{user?.name}</h2>
              <p className="text-slate-500 text-sm font-sans mt-0.5">{user?.email}</p>
              <button className="mt-3 text-blue-600 hover:text-blue-800 text-xs font-heading font-bold uppercase tracking-wider flex items-center justify-center sm:justify-start space-x-1.5 transition-colors">
                <Camera className="w-3.5 h-3.5" />
                <span>Change Photo</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-5 font-sans">
              <div>
                <label className="block text-xs font-heading font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!editing}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-400 text-sm shadow-inner"
                />
              </div>

              <div>
                <label className="block text-xs font-heading font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!editing}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-400 text-sm shadow-inner"
                />
              </div>

              <div>
                <label className="block text-xs font-heading font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Bio / Learning Goal
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  disabled={!editing}
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-400 text-sm resize-none shadow-inner"
                  placeholder="Tell us about your learning goals and cloud career aspiration..."
                />
              </div>

              <div className="flex items-center space-x-4 pt-2">
                {editing ? (
                  <>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-heading font-bold text-xs uppercase tracking-wider shadow-xs flex items-center space-x-2 transition-all"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-heading font-bold text-xs uppercase tracking-wider transition-all border border-slate-200"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-heading font-bold text-xs uppercase tracking-wider shadow-xs transition-all"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Account Settings */}
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6 sm:p-8">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6">Account Settings</h2>
          <div className="space-y-4 font-sans text-sm">
            <div className="flex items-center justify-between py-4 border-b border-slate-100">
              <div>
                <h3 className="font-heading font-bold text-slate-900">Change Password</h3>
                <p className="text-xs text-slate-500 mt-0.5">Update your account password</p>
              </div>
              <button className="text-blue-600 hover:text-blue-800 font-heading font-bold text-xs uppercase tracking-wider transition-colors">
                Change
              </button>
            </div>
            <div className="flex items-center justify-between py-4 border-b border-slate-100">
              <div>
                <h3 className="font-heading font-bold text-slate-900">Email Notifications</h3>
                <p className="text-xs text-slate-500 mt-0.5">Manage live batch reminders & course updates</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between py-4">
              <div>
                <h3 className="font-heading font-bold text-slate-900">Delete Account</h3>
                <p className="text-xs text-slate-500 mt-0.5">Permanently delete your account</p>
              </div>
              <button className="text-rose-600 hover:text-rose-700 font-heading font-bold text-xs uppercase tracking-wider transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
